import React, { useEffect, useRef, useCallback, useState } from 'react';

const GEOJSON_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/land-110m.json';

const SURVIVOR_REGION = { lat: 20, lon: 78 }; // India center

// Module-level cache to persist across navigations
let cachedGeoData = null;
let cachedLandPoints = null;
let cachedRoutes = null;
let cachedMeshNodes = null;
let isLoading = false;
let loadPromise = null;

const CITIES = [
  { lat: 40.7, lon: -74, name: 'new-york' },
  { lat: 34, lon: -118, name: 'los-angeles' },
  { lat: 51.5, lon: -0.1, name: 'london' },
  { lat: 48.9, lon: 2.3, name: 'paris' },
  { lat: 35.7, lon: 139.7, name: 'tokyo' },
  { lat: 31.2, lon: 121.5, name: 'shanghai' },
  { lat: 28.6, lon: 77.2, name: 'delhi' },
  { lat: 19.1, lon: 72.9, name: 'mumbai' },
  { lat: -33.9, lon: 151.2, name: 'sydney' },
  { lat: 55.8, lon: 37.6, name: 'moscow' },
  { lat: -23.5, lon: -46.6, name: 'sao-paulo' },
  { lat: 30, lon: 31.2, name: 'cairo' },
  { lat: 1.3, lon: 103.8, name: 'singapore' },
  { lat: 25.3, lon: 55.3, name: 'dubai' },
  { lat: 37.6, lon: 127, name: 'seoul' },
  { lat: 22.3, lon: 114.2, name: 'hong-kong' },
];

// Simple land check using bounding boxes (fallback)
const isLandFallback = (lat, lon) => {
  // North America
  if (lat > 25 && lat < 70 && lon > -130 && lon < -60) return true;
  // South America
  if (lat > -56 && lat < 12 && lon > -82 && lon < -34) return true;
  // Europe
  if (lat > 35 && lat < 71 && lon > -10 && lon < 40) return true;
  // Africa
  if (lat > -35 && lat < 37 && lon > -18 && lon < 52) return true;
  // Russia/Asia
  if (lat > 40 && lat < 75 && lon > 40 && lon < 180) return true;
  // India
  if (lat > 6 && lat < 36 && lon > 68 && lon < 98) return true;
  // China/East Asia
  if (lat > 18 && lat < 54 && lon > 73 && lon < 135) return true;
  // Southeast Asia
  if (lat > -11 && lat < 28 && lon > 93 && lon < 141) return true;
  // Japan
  if (lat > 30 && lat < 46 && lon > 129 && lon < 146) return true;
  // Australia
  if (lat > -45 && lat < -10 && lon > 112 && lon < 154) return true;
  // Middle East
  if (lat > 12 && lat < 42 && lon > 34 && lon < 63) return true;
  // UK/Ireland
  if (lat > 50 && lat < 59 && lon > -11 && lon < 2) return true;
  // Greenland
  if (lat > 59 && lat < 84 && lon > -73 && lon < -12) return true;
  return false;
};

const BlackoutGlobe = ({ darkMode }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const stateRef = useRef(null);
  const [isReady, setIsReady] = useState(!!cachedLandPoints);

  const getColors = useCallback((dark) => ({
    bg: dark ? '#000000' : '#ffffff',
    land: dark ? 'rgba(255, 255, 255,' : 'rgba(0, 0, 0,',
    landDead: dark ? 'rgba(50, 50, 50, 0.4)' : 'rgba(200, 200, 200, 0.4)',
    city: dark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.85)',
    arc: dark ? '255,255,255' : '0,0,0',
    survivor: dark ? '80,180,255' : '30,120,200',
    mesh: dark ? '100,200,255' : '30,120,200',
    meshDim: dark ? '40,40,40' : '210,210,210',
  }), []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const ctx = canvas.getContext('2d', { alpha: false });
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let width, height, centerX, centerY, radius;

    const resize = () => {
      width = container.clientWidth;
      height = container.clientHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      centerX = width / 2;
      centerY = height / 2;
      radius = Math.min(width, height) * 0.35;
    };
    resize();

    // Initialize state using cached data if available
    if (!stateRef.current) {
      stateRef.current = {
        stage: 1,
        stageStart: Date.now(),
        rotation: 0,
        landPoints: cachedLandPoints ? cachedLandPoints.map(p => ({...p, active: true, waveHit: null})) : [],
        routes: cachedRoutes ? cachedRoutes.map(r => ({...r, active: true, phase: Math.random()})) : [],
        meshNodes: cachedMeshNodes ? cachedMeshNodes.map(n => ({...n, active: false})) : [],
        loaded: !!cachedLandPoints
      };
    }
    const state = stateRef.current;

    // Load GeoJSON and generate points (with module-level caching)
    const loadGeo = async () => {
      // If already cached, just use it
      if (cachedLandPoints) {
        if (!state.landPoints.length) {
          state.landPoints = cachedLandPoints.map(p => ({...p, active: true, waveHit: null}));
          state.routes = cachedRoutes.map(r => ({...r, active: true, phase: Math.random()}));
          state.meshNodes = cachedMeshNodes.map(n => ({...n, active: false}));
        }
        state.loaded = true;
        setIsReady(true);
        return;
      }

      // If already loading, wait for it
      if (isLoading && loadPromise) {
        await loadPromise;
        state.landPoints = cachedLandPoints.map(p => ({...p, active: true, waveHit: null}));
        state.routes = cachedRoutes.map(r => ({...r, active: true, phase: Math.random()}));
        state.meshNodes = cachedMeshNodes.map(n => ({...n, active: false}));
        state.loaded = true;
        setIsReady(true);
        return;
      }

      isLoading = true;

      loadPromise = new Promise((resolve) => {
        let isLand = isLandFallback;

        const processGeoJSON = async () => {
          try {
            const res = await fetch(GEOJSON_URL);
            const topo = await res.json();
            const arcs = topo.arcs;
            const transform = topo.transform;
            const land = topo.objects.land;

            // Decode TopoJSON
            const decodeArc = (idx) => {
              const rev = idx < 0;
              const arc = arcs[rev ? ~idx : idx];
              const coords = [];
              let x = 0, y = 0;
              for (const [dx, dy] of arc) {
                x += dx; y += dy;
                coords.push([
                  x * transform.scale[0] + transform.translate[0],
                  y * transform.scale[1] + transform.translate[1]
                ]);
              }
              return rev ? coords.reverse() : coords;
            };

            const polygons = [];
            const extract = (geom) => {
              if (geom.type === 'GeometryCollection') {
                geom.geometries.forEach(extract);
              } else if (geom.type === 'Polygon' || geom.type === 'MultiPolygon') {
                const rings = geom.type === 'Polygon' ? [geom.arcs] : geom.arcs;
                for (const poly of rings) {
                  for (const ring of poly) {
                    const coords = ring.flatMap(decodeArc);
                    if (coords.length > 2) polygons.push(coords);
                  }
                }
              }
            };
            extract(land);

            // Point in polygon
            const pip = (lat, lon, poly) => {
              let inside = false;
              for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
                const [xi, yi] = poly[i], [xj, yj] = poly[j];
                if (((yi > lat) !== (yj > lat)) && (lon < (xj - xi) * (lat - yi) / (yj - yi) + xi)) {
                  inside = !inside;
                }
              }
              return inside;
            };

            isLand = (lat, lon) => {
              for (const poly of polygons) {
                if (pip(lat, lon, poly)) return true;
              }
              return false;
            };

            cachedGeoData = polygons;
          } catch (e) {
            console.warn('GeoJSON load failed, using fallback');
          }

          return isLand;
        };

        // Generate points in chunks to avoid blocking
        const generatePointsChunked = (isLandFn) => {
          return new Promise((resolvePoints) => {
            const points = [];
            let attempts = 0;
            const maxPoints = 5000;
            const maxAttempts = 50000;
            const chunkSize = 2000; // Process 2000 attempts per frame

            const processChunk = () => {
              const chunkEnd = Math.min(attempts + chunkSize, maxAttempts);

              while (attempts < chunkEnd && points.length < maxPoints) {
                attempts++;
                const lat = (Math.random() - 0.5) * 160;
                const lon = (Math.random() - 0.5) * 360;
                if (isLandFn(lat, lon)) {
                  const distToSurvivor = Math.hypot(lat - SURVIVOR_REGION.lat, lon - SURVIVOR_REGION.lon);
                  points.push({
                    lat, lon,
                    size: 0.8 + Math.random() * 0.6,
                    brightness: 0.5 + Math.random() * 0.5,
                    isSurvivor: distToSurvivor < 15
                  });
                }
              }

              if (points.length < maxPoints && attempts < maxAttempts) {
                setTimeout(processChunk, 0); // Yield to main thread
              } else {
                resolvePoints(points);
              }
            };

            setTimeout(processChunk, 0);
          });
        };

        processGeoJSON().then(async (isLandFn) => {
          cachedLandPoints = await generatePointsChunked(isLandFn);

          // Generate routes between cities (fast, no chunking needed)
          const routes = [];
          for (let i = 0; i < CITIES.length; i++) {
            for (let j = i + 1; j < CITIES.length; j++) {
              if (Math.random() < 0.2) {
                routes.push({
                  from: CITIES[i],
                  to: CITIES[j],
                  opacity: 0.5 + Math.random() * 0.4
                });
              }
            }
          }
          cachedRoutes = routes;

          // Generate mesh nodes (fast, no chunking needed)
          const meshNodes = [];
          for (let i = 0; i < 35; i++) {
            const angle = (i / 35) * Math.PI * 2 + Math.random() * 0.3;
            const dist = 70 + Math.random() * 150;
            meshNodes.push({
              x: Math.cos(angle) * dist,
              y: Math.sin(angle) * dist,
              connections: []
            });
          }
          meshNodes.forEach((n, i) => {
            const sorted = meshNodes
              .map((o, j) => ({ j, d: Math.hypot(n.x - o.x, n.y - o.y) }))
              .filter(x => x.j !== i)
              .sort((a, b) => a.d - b.d);
            n.connections = sorted.slice(0, 3).map(x => x.j);
          });
          cachedMeshNodes = meshNodes;

          isLoading = false;
          resolve();
        });
      });

      await loadPromise;

      state.landPoints = cachedLandPoints.map(p => ({...p, active: true, waveHit: null}));
      state.routes = cachedRoutes.map(r => ({...r, active: true, phase: Math.random()}));
      state.meshNodes = cachedMeshNodes.map(n => ({...n, active: false}));
      state.loaded = true;
      setIsReady(true);
    };

    // Use requestIdleCallback for non-blocking load, or setTimeout as fallback
    const scheduleLoad = window.requestIdleCallback || ((cb) => setTimeout(cb, 1));
    scheduleLoad(() => loadGeo());

    let colors = getColors(darkMode);

    const project = (lat, lon, rot) => {
      const phi = (lat * Math.PI) / 180;
      const theta = ((lon + rot) * Math.PI) / 180;
      const cp = Math.cos(phi);
      const x = radius * cp * Math.sin(theta);
      const y = -radius * Math.sin(phi);
      const z = radius * cp * Math.cos(theta);
      return { x: centerX + x, y: centerY + y, z, visible: z > -radius * 0.1, depth: (z + radius) / (2 * radius) };
    };

    const lerp = (a, b, t) => a + (b - a) * t;

    const drawLand = (rot, showSurvivorGlow) => {
      state.landPoints.forEach(p => {
        const pos = project(p.lat, p.lon, rot);
        if (!pos.visible || pos.z < 0) return;

        const alpha = p.brightness * pos.depth;
        let color;

        if (!p.active) {
          color = colors.landDead;
        } else if (p.isSurvivor && showSurvivorGlow) {
          color = `rgba(${colors.survivor}, ${alpha})`;
        } else {
          color = `${colors.land}${alpha})`;
        }

        ctx.fillStyle = color;
        const s = p.size * pos.depth;
        ctx.fillRect(pos.x - s/2, pos.y - s/2, s, s);
      });
    };

    const drawCities = (rot, fadeOut = 1) => {
      CITIES.forEach(city => {
        const pos = project(city.lat, city.lon, rot);
        if (!pos.visible || pos.z < 0) return;
        ctx.fillStyle = colors.city;
        ctx.globalAlpha = fadeOut * pos.depth;
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, 2.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      });
    };

    const drawArc = (from, to, rot, opacity, phase) => {
      const steps = 60;
      const pts = [];

      for (let i = 0; i <= steps; i++) {
        const t = i / steps;
        const lat = lerp(from.lat, to.lat, t);
        const lon = lerp(from.lon, to.lon, t);
        const h = Math.sin(t * Math.PI) * 18;
        const pos = project(lat, lon, rot);
        if (pos.visible) pts.push({ ...pos, h, t });
      }

      if (pts.length < 2) return;

      // Draw arc segments with animated brightness wave
      for (let i = 1; i < pts.length; i++) {
        const p1 = pts[i - 1];
        const p2 = pts[i];

        // Create a traveling brightness wave along the arc
        const segmentT = (i - 1) / (pts.length - 1);
        const wave = (phase + segmentT) % 1;
        const brightness = 0.12 + Math.pow(Math.sin(wave * Math.PI), 2) * 0.25;

        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y - p1.h * p1.depth);
        ctx.lineTo(p2.x, p2.y - p2.h * p2.depth);
        ctx.strokeStyle = `rgba(${colors.arc}, ${opacity * brightness})`;
        ctx.lineWidth = 0.6;
        ctx.stroke();
      }
    };

    const drawRoutes = (rot, opacity = 1) => {
      state.routes.forEach(r => {
        if (!r.active) return;
        r.phase = (r.phase + 0.008) % 1;
        drawArc(r.from, r.to, rot, r.opacity * opacity, r.phase);
      });
    };

    const drawSurvivorGlow = (rot, intensity) => {
      const pos = project(SURVIVOR_REGION.lat, SURVIVOR_REGION.lon, rot);
      if (!pos.visible || pos.z < 0) return;

      const grad = ctx.createRadialGradient(pos.x, pos.y, 0, pos.x, pos.y, 70);
      grad.addColorStop(0, `rgba(${colors.survivor}, ${0.2 * intensity})`);
      grad.addColorStop(0.5, `rgba(${colors.survivor}, ${0.08 * intensity})`);
      grad.addColorStop(1, `rgba(${colors.survivor}, 0)`);
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, 70, 0, Math.PI * 2);
      ctx.fill();
    };

    const drawMesh = (opacity) => {
      const now = Date.now();
      ctx.save();
      ctx.translate(centerX, centerY);

      // Draw connections with subtle pulse
      state.meshNodes.forEach((n, i) => {
        n.connections.forEach(j => {
          if (j < i) return;
          const o = state.meshNodes[j];
          const active = n.active && o.active;

          if (active) {
            // Animated brightness pulse along the line
            const phase = ((now * 0.001) + i * 0.2) % 1;
            const pulse = 0.25 + Math.sin(phase * Math.PI * 2) * 0.15;
            ctx.strokeStyle = `rgba(${colors.mesh}, ${pulse * opacity})`;
            ctx.lineWidth = 1;
          } else {
            ctx.strokeStyle = `rgba(${colors.meshDim}, ${0.15 * opacity})`;
            ctx.lineWidth = 0.5;
          }

          ctx.beginPath();
          ctx.moveTo(n.x, n.y);
          ctx.lineTo(o.x, o.y);
          ctx.stroke();
        });
      });

      // Draw nodes - simple dots
      state.meshNodes.forEach(n => {
        const s = n.active ? 3 : 2;
        ctx.fillStyle = n.active
          ? `rgba(${colors.mesh}, ${opacity * 0.9})`
          : `rgba(${colors.meshDim}, ${opacity * 0.4})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, s, 0, Math.PI * 2);
        ctx.fill();
      });

      ctx.restore();
    };

    const ease = t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

    const animate = () => {
      if (!state.loaded) {
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      const now = Date.now();
      const elapsed = (now - state.stageStart) / 1000;
      colors = getColors(darkMode);

      ctx.fillStyle = colors.bg;
      ctx.fillRect(0, 0, width, height);

      state.rotation += 0.12;
      const rot = state.rotation;

      // Stage 1: Normal (8s)
      if (state.stage === 1) {
        drawLand(rot, false);
        drawRoutes(rot);
        drawCities(rot);

        if (elapsed > 8) {
          state.stage = 2;
          state.stageStart = now;
        }
      }

      // Stage 2: Blackout (15s)
      else if (state.stage === 2) {
        const progress = Math.min(elapsed / 15, 1);
        const ep = ease(progress);

        // Deactivate points
        const deactivate = Math.floor(ep * state.landPoints.length * 0.92);
        for (let i = 0; i < deactivate; i++) {
          if (!state.landPoints[i].isSurvivor) state.landPoints[i].active = false;
        }

        // Deactivate routes
        const deactivateRoutes = Math.floor(ep * state.routes.length);
        for (let i = 0; i < deactivateRoutes; i++) {
          state.routes[i].active = false;
        }

        drawLand(rot, false);
        drawRoutes(rot, 1 - ep);
        drawCities(rot, 1 - ep);

        if (elapsed > 15) {
          state.stage = 3;
          state.stageStart = now;
          state.landPoints.forEach(p => { if (!p.isSurvivor) p.active = false; });
          state.routes.forEach(r => r.active = false);
        }
      }

      // Stage 3: Survivor glow (6s)
      else if (state.stage === 3) {
        const progress = Math.min(elapsed / 6, 1);
        const ep = ease(progress);

        drawLand(rot, true);
        drawSurvivorGlow(rot, ep);

        if (elapsed > 6) {
          state.stage = 4;
          state.stageStart = now;
        }
      }

      // Stage 4: Mesh activation (8s)
      else if (state.stage === 4) {
        const progress = Math.min(elapsed / 8, 1);

        const globeFade = 1 - ease(Math.min(progress * 2, 1));
        const meshFade = ease(progress);

        // Activate nodes
        const toActivate = Math.floor(progress * state.meshNodes.length);
        for (let i = 0; i < toActivate; i++) state.meshNodes[i].active = true;

        if (globeFade > 0.01) {
          drawLand(rot, true);
          drawSurvivorGlow(rot, globeFade);
        }
        drawMesh(meshFade);

        if (elapsed > 8) {
          state.stage = 5;
          state.stageStart = now;
        }
      }

      // Stage 5: Return to globe (4s)
      else if (state.stage === 5) {
        const progress = Math.min(elapsed / 4, 1);
        const meshFade = 1 - ease(progress);
        const globeFade = ease(progress);

        if (meshFade > 0.01) drawMesh(meshFade);
        drawLand(rot, true);
        drawSurvivorGlow(rot, globeFade);

        if (elapsed > 4) {
          state.stage = 6;
          state.stageStart = now;
        }
      }

      // Stage 6: Blue wave spreads, everything comes back online (10s)
      else if (state.stage === 6) {
        const progress = Math.min(elapsed / 10, 1);
        const waveRadius = progress * 200; // Wave expands from India

        // Reactivate points as wave reaches them
        state.landPoints.forEach(p => {
          const distToSurvivor = Math.hypot(p.lat - SURVIVOR_REGION.lat, p.lon - SURVIVOR_REGION.lon);
          if (distToSurvivor < waveRadius) {
            p.active = true;
            p.waveHit = p.waveHit || elapsed;
          }
        });

        // Draw land with spreading blue effect
        state.landPoints.forEach(p => {
          const pos = project(p.lat, p.lon, rot);
          if (!pos.visible || pos.z < 0) return;

          const alpha = p.brightness * pos.depth;
          const distToSurvivor = Math.hypot(p.lat - SURVIVOR_REGION.lat, p.lon - SURVIVOR_REGION.lon);

          if (p.active) {
            // Blue tint fades as wave passes
            const timeSinceHit = p.waveHit ? elapsed - p.waveHit : 0;
            const blueFade = Math.max(0, 1 - timeSinceHit / 3);

            if (blueFade > 0.1) {
              ctx.fillStyle = `rgba(${colors.survivor}, ${alpha * blueFade})`;
            } else {
              ctx.fillStyle = `${colors.land}${alpha})`;
            }
          } else {
            ctx.fillStyle = colors.landDead;
          }

          const s = p.size * pos.depth;
          ctx.fillRect(pos.x - s/2, pos.y - s/2, s, s);
        });

        // Draw expanding wave ring
        if (progress < 0.8) {
          const wavePos = project(SURVIVOR_REGION.lat, SURVIVOR_REGION.lon, rot);
          if (wavePos.visible && wavePos.z > 0) {
            const ringRadius = waveRadius * 1.5;
            const ringOpacity = (1 - progress) * 0.3;
            ctx.strokeStyle = `rgba(${colors.survivor}, ${ringOpacity})`;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(wavePos.x, wavePos.y, ringRadius, 0, Math.PI * 2);
            ctx.stroke();
          }
        }

        // Gradually bring back routes
        const routeProgress = Math.max(0, (progress - 0.3) / 0.7);
        if (routeProgress > 0) {
          const routesToActivate = Math.floor(routeProgress * state.routes.length);
          for (let i = 0; i < routesToActivate; i++) {
            state.routes[i].active = true;
          }
          drawRoutes(rot, ease(routeProgress));
        }

        // Draw cities coming back
        const cityProgress = Math.max(0, (progress - 0.4) / 0.6);
        if (cityProgress > 0) {
          drawCities(rot, ease(cityProgress));
        }

        // Fade out survivor glow as everything normalizes
        const glowFade = 1 - ease(Math.max(0, (progress - 0.6) / 0.4));
        if (glowFade > 0.01) {
          drawSurvivorGlow(rot, glowFade);
        }

        // Seamless reset to stage 1
        if (elapsed > 10) {
          state.stage = 1;
          state.stageStart = now;
          // Reset all points and routes for clean loop
          state.landPoints.forEach(p => { p.active = true; p.waveHit = null; });
          state.routes.forEach(r => { r.active = true; });
          state.meshNodes.forEach(n => { n.active = false; });
        }
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    window.addEventListener('resize', resize);
    animate();

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', resize);
      stateRef.current = null; // Reset state on unmount for clean remount
    };
  }, [darkMode, getColors, isReady]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative"
      style={{ background: darkMode ? '#000' : '#fff', pointerEvents: 'none' }}
    >
      <canvas ref={canvasRef} className="block w-full h-full" />
      {/* Pre-loader overlay - Dual Arc */}
      {!isReady && (
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ background: darkMode ? '#000' : '#fff' }}
        >
          <div className="relative w-12 h-12">
            <div
              className="absolute inset-0 rounded-full border-2 border-transparent animate-spin"
              style={{
                borderTopColor: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                borderRightColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                animationDuration: '1.2s'
              }}
            />
            <div
              className="absolute inset-1 rounded-full border-2 border-transparent animate-spin"
              style={{
                borderBottomColor: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                borderLeftColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                animationDuration: '1.2s',
                animationDirection: 'reverse'
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BlackoutGlobe;
